import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/features/auth/authStore';
import toast from 'react-hot-toast';

export interface WebSocketMessage {
  type: 'new_message' | 'new_lead' | 'lead_updated' | 'conversation_updated' | 'system_update';
  data: any;
  timestamp: string;
}

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const { user, token } = useAuthStore();

  const connect = useCallback(() => {
    if (!token || !user) {
      console.log('No auth token or user, skipping WebSocket connection');
      return;
    }

    const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:42817';
    
    try {
      socketRef.current = io(wsUrl, {
        auth: {
          token: token
        },
        autoConnect: true,
        transports: ['websocket', 'polling']
      });

      const socket = socketRef.current;

      socket.on('connect', () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        
        // Join user-specific room
        socket.emit('join-room', `user-${user.id}`);
      });

      socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
        setIsConnected(false);
        
        if (reason === 'io server disconnect') {
          // Server-initiated disconnect, reconnect manually
          socket.connect();
        }
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        setConnectionError(error.message);
        setIsConnected(false);
      });

      socket.on('error', (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error occurred');
      });

      // Handle reconnection
      socket.on('reconnect', (attemptNumber) => {
        console.log(`WebSocket reconnected after ${attemptNumber} attempts`);
        setIsConnected(true);
        setConnectionError(null);
        toast.success('Connection restored');
      });

      socket.on('reconnect_error', (error) => {
        console.error('WebSocket reconnection error:', error);
        setConnectionError('Failed to reconnect');
      });

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setConnectionError('Failed to create connection');
    }
  }, [token, user]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setConnectionError(null);
    }
  }, []);

  const subscribe = useCallback((eventType: string, handler: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(eventType, handler);
      
      return () => {
        if (socketRef.current) {
          socketRef.current.off(eventType, handler);
        }
      };
    }
    return () => {};
  }, []);

  const emit = useCallback((eventType: string, data: any) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit(eventType, data);
    }
  }, [isConnected]);

  // Auto-connect when component mounts and user is authenticated
  useEffect(() => {
    if (token && user) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect, token, user]);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    subscribe,
    emit
  };
};

// Hook for real-time notifications
export const useRealTimeNotifications = () => {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    // New message notification — only for inbound (customer) messages, not our
    // own outbound/AI replies which also broadcast on this channel.
    const unsubscribeNewMessage = subscribe('new_message', (data) => {
      if (data?.direction && data.direction !== 'inbound') return;
      toast.success(`New message from ${data?.customerName || 'a customer'}`, {
        duration: 5000,
        icon: '💬'
      });
    });

    // New lead notification
    const unsubscribeNewLead = subscribe('new_lead', (data) => {
      toast.success(`New lead: ${data.customerName}`, {
        duration: 5000,
        icon: '👤'
      });
    });

    // Lead conversion notification
    const unsubscribeLeadConverted = subscribe('lead_converted', (data) => {
      toast.success(`Lead converted: ${data.customerName} - $${data.value}`, {
        duration: 6000,
        icon: '🎉'
      });
    });

    // Platform connection notifications
    const unsubscribeFacebookConnected = subscribe('facebook_connected', (data) => {
      toast.success(`${data.totalCount} Facebook page${data.totalCount !== 1 ? 's' : ''} connected!`, {
        icon: '📘'
      });
    });

    const unsubscribeInstagramConnected = subscribe('instagram_connected', (data) => {
      toast.success(`${data.totalCount} Instagram account${data.totalCount !== 1 ? 's' : ''} connected!`, {
        icon: '📷'
      });
    });

    const unsubscribeWhatsAppConnected = subscribe('whatsapp_connected', (data) => {
      toast.success(`WhatsApp Business connected: ${data?.account?.businessName ?? 'your account'}`, {
        icon: '💬'
      });
    });

    const unsubscribePlatformDisconnected = subscribe('platform_disconnected', (data) => {
      toast(`${data.platform} account disconnected`, {
        icon: '🔌'
      });
    });

    // System notifications
    const unsubscribeSystemUpdate = subscribe('system_update', (data) => {
      if (data.type === 'integration_connected') {
        toast.success(`${data.platform} integration connected`, {
          icon: '🔗'
        });
      } else if (data.type === 'integration_disconnected') {
        toast.error(`${data.platform} integration disconnected`, {
          icon: '⚠️'
        });
      }
    });

    return () => {
      unsubscribeNewMessage();
      unsubscribeNewLead();
      unsubscribeLeadConverted();
      unsubscribeFacebookConnected();
      unsubscribeInstagramConnected();
      unsubscribeWhatsAppConnected();
      unsubscribePlatformDisconnected();
      unsubscribeSystemUpdate();
    };
  }, [subscribe]);
};

// Hook for real-time dashboard updates
export const useRealTimeDashboard = () => {
  const { subscribe } = useWebSocket();
  const [liveMetrics, setLiveMetrics] = useState({
    activeConnections: 0,
    messagesLastHour: 0,
    pendingLeads: 0,
    systemHealth: 'healthy' as 'healthy' | 'warning' | 'error'
  });

  useEffect(() => {
    const unsubscribeLiveStats = subscribe('live_stats_update', (data) => {
      setLiveMetrics(data);
    });

    const unsubscribeMetricUpdate = subscribe('metric_update', (data) => {
      setLiveMetrics(prev => ({
        ...prev,
        [data.metric]: data.value
      }));
    });

    return () => {
      unsubscribeLiveStats();
      unsubscribeMetricUpdate();
    };
  }, [subscribe]);

  return { liveMetrics };
};

// Hook for real-time conversation updates
export const useRealTimeConversations = (conversationId?: string) => {
  const { subscribe, emit } = useWebSocket();
  const [newMessages, setNewMessages] = useState<any[]>([]);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);

  useEffect(() => {
    if (!conversationId) return;

    // Join conversation room
    emit('join-conversation', conversationId);

    const unsubscribeNewMessage = subscribe('conversation_message', (data) => {
      if (data.conversationId === conversationId) {
        setNewMessages(prev => [...prev, data.message]);
      }
    });

    const unsubscribeTyping = subscribe('user_typing', (data) => {
      if (data.conversationId === conversationId) {
        setTypingUsers(prev => [...prev.filter(id => id !== data.userId), data.userId]);
        
        // Remove typing indicator after 3 seconds
        setTimeout(() => {
          setTypingUsers(prev => prev.filter(id => id !== data.userId));
        }, 3000);
      }
    });

    const unsubscribeStopTyping = subscribe('user_stop_typing', (data) => {
      if (data.conversationId === conversationId) {
        setTypingUsers(prev => prev.filter(id => id !== data.userId));
      }
    });

    return () => {
      emit('leave-conversation', conversationId);
      unsubscribeNewMessage();
      unsubscribeTyping();
      unsubscribeStopTyping();
    };
  }, [conversationId, subscribe, emit]);

  const sendTypingIndicator = useCallback(() => {
    if (conversationId) {
      emit('typing', conversationId);
    }
  }, [conversationId, emit]);

  const stopTypingIndicator = useCallback(() => {
    if (conversationId) {
      emit('stop_typing', conversationId);
    }
  }, [conversationId, emit]);

  return {
    newMessages,
    typingUsers,
    sendTypingIndicator,
    stopTypingIndicator,
    clearNewMessages: () => setNewMessages([])
  };
};

// Hook for real-time leads updates
export const useRealTimeLeads = () => {
  const { subscribe } = useWebSocket();
  const [leadUpdates, setLeadUpdates] = useState<any[]>([]);

  useEffect(() => {
    const unsubscribeNewLead = subscribe('new_lead', (data) => {
      setLeadUpdates(prev => [...prev, { type: 'new', data, timestamp: new Date() }]);
    });

    const unsubscribeLeadUpdate = subscribe('lead_updated', (data) => {
      setLeadUpdates(prev => [...prev, { type: 'updated', data, timestamp: new Date() }]);
    });

    const unsubscribeLeadConverted = subscribe('lead_converted', (data) => {
      setLeadUpdates(prev => [...prev, { type: 'converted', data, timestamp: new Date() }]);
    });

    return () => {
      unsubscribeNewLead();
      unsubscribeLeadUpdate();
      unsubscribeLeadConverted();
    };
  }, [subscribe]);

  return {
    leadUpdates,
    clearUpdates: () => setLeadUpdates([])
  };
};